import { useState } from "react";
import toast from "react-hot-toast";

const LevelAddModal = ({ showLevelModal, setShowLevelModal, onSave }) => {
  const [levels, setLevels] = useState([{ id: Date.now(), name: "", sequence: 0 }]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  if (!showLevelModal) return null;

  // ----------------------------
  // Drag & Drop Logic
  // ----------------------------
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.setData("text/plain", index);
    e.target.style.opacity = "0.1";
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedIndex(null);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);

    if (dragIndex !== dropIndex) {
      setLevels((prev) => {
        const updated = [...prev];
        const [draggedItem] = updated.splice(dragIndex, 1);
        updated.splice(dropIndex, 0, draggedItem);
        return updated.map((item, idx) => ({ ...item, sequence: idx }));
      });
    }
  };

  const addLevel = () => setLevels((prev) => [...prev, { id: Date.now(), name: "", sequence: prev.length }]);
  const removeLevel = (id) => setLevels((prev) => prev.filter((l) => l.id !== id).map((item, idx) => ({ ...item, sequence: idx })));

  const handleSaveLevels = () => {
    if (levels.some((l) => !l.name.trim())) {
      toast.error("Level names cannot be empty");
      return;
    }

    const formatted = levels.map((lvl) => ({ name: lvl.name.trim(), sequence: lvl.sequence }));
    onSave(formatted);
    setShowLevelModal(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      {/* Modal */}
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Create Hierarchy Levels</h2>

        {/* Add Button */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-medium text-gray-700">Levels</h3>
          <button
            onClick={addLevel}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            + Add Level
          </button>
        </div>

        {/* Levels List */}
        <div>
          {levels.map((lvl, index) => (
            <div
              key={lvl.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              className={`p-3 mb-2 bg-gray-100 rounded-md shadow flex items-center gap-3 cursor-move ${draggedIndex === index ? "opacity-50" : "opacity-100"}`}
            >
              <div className="text-gray-500 cursor-grab">☰</div>
              <input
                type="text"
                value={lvl.name}
                onChange={(e) => {
                  const updated = [...levels];
                  updated[index].name = e.target.value;
                  setLevels(updated);
                }}
                placeholder={`Level ${index + 1} name`}
                className="flex-1 border rounded px-3 py-1 focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={() => removeLevel(lvl.id)}
                className="text-red-600 text-lg hover:text-red-800"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={() => setShowLevelModal(false)}
            className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveLevels}
            disabled={levels.some((l) => !l.name.trim())}
            className={`px-4 py-2 text-sm text-white rounded ${levels.some((l) => !l.name.trim()) ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
          >
            Save Levels
          </button>
        </div>
      </div>
    </div>
  );
};

export default LevelAddModal;
